import React, { useState, useEffect } from 'react';
import apiClient from '../services/api';
import '../Dashboard.css';

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isDesktop = windowWidth >= 1024;

  const handleLogin = async () => {
    if (!email || !password) {
      setError('ກະລຸນາໃສ່ອີເມວແລະລະຫັດຜ່ານ');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await apiClient.authLogin({ email, password });
      if (res.success) {
        localStorage.setItem('rf_token', res.data.token);
        onLogin();
      } else {
        setError(`❌ ${res.error || 'ລະຫັດຜ່ານຜິດພາດ ກະລຸນາລອງໃໝ່'}`);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(`❌ ${err.message || 'ເກີດຂໍ້ຜິດພາດ ກະລຸນາລອງໃໝ່'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      position: 'relative',
      overflowX: 'hidden',
      overflowY: 'auto',
      backgroundColor: '#090d16',
      fontFamily: "'Noto Sans Laos', 'Noto Sans Lao', sans-serif"
    }}>
      {/* Background - Deep Premium Mesh Gradient */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(135deg, #090d16 0%, #0f1c3f 50%, #05090e 100%)',
        zIndex: 1,
      }} />

      {/* Glowing blurred blobs for depth */}
      <div style={{
        position: 'absolute',
        width: '650px',
        height: '650px',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.22) 0%, rgba(59, 130, 246, 0) 70%)',
        top: '-150px',
        left: '-100px',
        filter: 'blur(60px)',
        zIndex: 2,
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute',
        width: '650px',
        height: '650px',
        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.18) 0%, rgba(99, 102, 241, 0) 70%)',
        bottom: '-150px',
        right: '-100px',
        filter: 'blur(70px)',
        zIndex: 2,
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(168, 85, 247, 0.12) 0%, rgba(168, 85, 247, 0) 70%)',
        top: '25%',
        left: '20%',
        filter: 'blur(90px)',
        zIndex: 2,
        pointerEvents: 'none',
      }} />

      {/* Subtle tech grid pattern overlay */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.05) 1.5px, transparent 1.5px)',
        backgroundSize: '32px 32px',
        zIndex: 3,
        opacity: 0.8,
        pointerEvents: 'none',
      }} />

      {/* Left Side - Branding */}
      <div style={{
        display: isDesktop ? 'flex' : 'none',
        width: '50%',
        position: 'relative',
        zIndex: 10,
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px',
        color: '#ffffff',
        textAlign: 'center',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* PWT Logo */}
          <div style={{
            marginBottom: '32px',
            display: 'flex',
            justifyContent: 'center',
          }}>
            <img
              src="/pwt_logo.png"
              alt="PWT Logo"
              style={{
                width: '260px',
                height: 'auto',
                filter: 'drop-shadow(0 15px 30px rgba(0,0,0,0.25))',
                transition: 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            />
          </div>

          {/* Title */}
          <h1 style={{
            fontSize: '44px',
            fontWeight: '800',
            marginBottom: '12px',
            textAlign: 'center',
            letterSpacing: '1.5px',
            textShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}>
            SMART ROADFUND SYSTEM
          </h1>
          <p style={{
            fontSize: '19px',
            textAlign: 'center',
            opacity: 0.95,
            maxWidth: '360px',
            lineHeight: '1.6',
            margin: '0 auto',
            textShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}>
            ລະບົບຄຸ້ມຄອງລາຍຮັບທັນສະໄຫມກອງທຶນທາງ
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div style={{
        width: isDesktop ? '50%' : '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        position: 'relative',
        zIndex: 10,
        backgroundColor: 'transparent',
      }}>
        <div style={{ width: '100%', maxWidth: '420px' }}>
          {/* Mobile Logo */}
          {!isDesktop && (
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <div style={{
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'center',
              }}>
                <img
                  src="/pwt_logo.png"
                  alt="PWT Logo"
                  style={{
                    width: '180px',
                    height: 'auto',
                    filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.3))',
                  }}
                />
              </div>
              <h1 style={{
                fontSize: '28px',
                fontWeight: '800',
                color: '#ffffff',
                marginBottom: '8px',
                letterSpacing: '1.2px',
                textShadow: '0 4px 12px rgba(0,0,0,0.2)',
              }}>
                SMART ROADFUND SYSTEM
              </h1>
              <p style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '14px',
                margin: '0 auto',
                maxWidth: '300px',
                lineHeight: '1.5',
                textShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}>
                ລະບົບຄຸ້ມຄອງລາຍຮັບທັນສະໄຫມກອງທຶນທາງ
              </p>
            </div>
          )}

          {/* Login Card */}
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255, 255, 255, 0.18)',
            padding: isDesktop ? '48px 40px' : '36px 28px',
          }}>
            <div style={{ marginBottom: '32px', textAlign: 'center' }}>
              <div style={{
                width: '64px',
                height: '64px',
                backgroundColor: '#eff6ff',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '30px',
                boxShadow: '0 4px 10px rgba(59, 130, 246, 0.1)',
              }}>
                🔐
              </div>
              <h2 style={{
                fontSize: '26px',
                fontWeight: '700',
                color: '#0f172a',
                marginBottom: '8px',
              }}>
                ເຂົ້າສູ່ລະບົບ
              </h2>
              <p style={{
                color: '#64748b',
                fontSize: '14px',
              }}>
                ປ້ອນລາຍລະອຽດຂອງທ່ານເພື່ອດຳເນີນການຕໍ່
              </p>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Email Input */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: '#334155',
                  fontWeight: '600',
                  fontSize: '14px',
                }}>
                  <span>📧</span>
                  <span>ອີເມວ (Email)</span>
                </label>
                <input
                  type="email"
                  placeholder="ປ້ອນອີເມວຂອງທ່ານ"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    backgroundColor: '#f8fafc',
                    outline: 'none',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    transition: 'all 200ms ease',
                  }}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.15)';
                    e.target.style.backgroundColor = '#ffffff';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#cbd5e1';
                    e.target.style.boxShadow = 'none';
                    e.target.style.backgroundColor = '#f8fafc';
                  }}
                  required
                />
              </div>

              {/* Password Input */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: '#334155',
                  fontWeight: '600',
                  fontSize: '14px',
                }}>
                  <span>🔑</span>
                  <span>ລະຫັດຜ່ານ (Password)</span>
                </label>
                <input
                  type="password"
                  placeholder="ປ້ອນລະຫັດຜ່ານຂອງທ່ານ"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    backgroundColor: '#f8fafc',
                    outline: 'none',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    transition: 'all 200ms ease',
                  }}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.15)';
                    e.target.style.backgroundColor = '#ffffff';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#cbd5e1';
                    e.target.style.boxShadow = 'none';
                    e.target.style.backgroundColor = '#f8fafc';
                  }}
                  required
                />
              </div>

              {/* Error Message */}
              {error && (
                <div style={{
                  backgroundColor: '#fee2e2',
                  border: '1px solid #fecaca',
                  color: '#991b1b',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  fontSize: '13px',
                }}>
                  {error}
                </div>
              )}

              {/* Login Button */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: '#ffffff',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '15px',
                  transition: 'all 200ms ease',
                  opacity: loading ? 0.7 : 1,
                  marginTop: '8px',
                }}
                 onMouseEnter={(e) => {
                   if (!loading) {
                     e.target.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.35)';
                     e.target.style.transform = 'translateY(-1px)';
                   }
                 }}
                 onMouseLeave={(e) => {
                   e.target.style.boxShadow = 'none';
                   e.target.style.transform = 'none';
                  }}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <svg
                      style={{ animation: 'spin 1s linear infinite', width: '16px', height: '16px' }}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
                      <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" opacity="0.75" />
                    </svg>
                    ກຳລັງເຂົ້າສູ່ລະບົບ...
                  </span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    🚀 ເຂົ້າສູ່ລະບົບ
                  </span>
                )}
              </button>

              {/* Reset Password Link */}
              <div style={{ textAlign: 'center', marginTop: '8px' }}>
                <a 
                  href="/reset-password" 
                  style={{
                    fontSize: '12px',
                    color: '#2563eb',
                    textDecoration: 'none',
                    cursor: 'pointer',
                  }} 
                  onMouseEnter={(e) => e.target.style.color = '#1d4ed8'} 
                  onMouseLeave={(e) => e.target.style.color = '#2563eb'}
                >
                  🔄 ລືມລະຫັດຜ່ານ?
                </a>
              </div>
            </form>

            {/* Footer */}
            <div style={{
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: '1px solid #e2e8f0',
              fontSize: '11px',
              color: '#64748b',
              textAlign: 'center',
            }}>
              <p style={{ margin: '0 0 4px 0' }}>© 2025 Road Refund System. All rights reserved.</p>
              <p style={{ margin: 0 }}>Powered by OLV Innovation Company Limited</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
